import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import { useInView } from "react-intersection-observer";
import { About, Management, KeyMilestones } from "../components/overview";
import type { TenantService } from "../../../shared/types";
import { getTenantServices } from "../../../shared/services/apis";
import { EmployeeReviews } from "../components/brandTruest/employeeReviews";
// import { ClientReviews } from "../components/brandTruest/clientReviews";
import OnlinePresence from "../components/brandTruest/OnlinePresence/OnlinePresence";
import { NewsFeed } from "../components/brandTruest/NewsFeed";
import LinkedinTimeline from "../components/brandTruest/LinkedinTimeline/LinkedinTimeline";
import TailwindsHeadwindsSection from "../components/opportunities/tailwindAndheadwinds/TailwindAndHeadwinds";
import GrowthOpportunities from "../components/opportunities/growthOpportunities/GrowthOpportunities";
import { Layout } from "../../../layout";
import { OwnershipSection } from "../components/Shareholders/Ownership";
import { PreviousOwners } from "../components/Shareholders/PreviousOwners";
import CompetitorMap from "../components/competitiveLandscapes/competitorsMap/CompetitorsMap";
import Overview from "../components/competitiveLandscapes/overview/Overview";
import { KeyCompetitorCard } from "../components/competitiveLandscapes/keyCompetitor/KeyCompetitor";
import MAOpportunities from "../components/opportunities/m&aOpportunities/M&AOpportunities";
import { FiHome, FiX } from "react-icons/fi";
import { BsThreeDotsVertical } from "react-icons/bs";
import { RiskScoreLayout } from "../components/riskscore/RiskScoreLayout";
import { RealStateComponent } from "../components/brandTruest/realState";
import IncomeBenchmarking from "../components/overview/FinancialsOverview/IncomeBenchmarking/IncomeBenchmarking";
import {
  FinancialProfile,
  PortfolioReview,
  ProductLevelInformation,
  CreditAssessmentData,
  AdditionalData,
  ESGAnalysis,
} from "../components/CreditAnalysis";
import KycKub from "../components/KYC&KYB";
import RiskParameters from "../components/RiskParameters";
import { ClientReviews } from "../components/brandTruest/clientReviews";
import BankRiskScore from "../components/BankRiskScore";

const ObservedSection = ({
  sectionKey,
  children,
  onInView,
  className = "",
}: {
  sectionKey: string;
  children: React.ReactNode;
  onInView: (key: string) => void;
  className?: string;
}) => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    rootMargin: "0px 0px 0% 0px",
  });

  useEffect(() => {
    if (inView && onInView) {
      onInView(sectionKey);
    }
  }, [inView]);

  return (
    <section
      ref={ref}
      id={`section-${sectionKey}`}
      className={`min-h-screen scroll-mt-20 ${className}`}
    >
      <div className="mt-6">{children}</div>
    </section>
  );
};

export function Tenant() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tenantServices, setTenantServices] = useState<TenantService[]>([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");

  const tenantServiceId = searchParams.get("tenantServiceId") || "";
  const tenantServiceOptionId = searchParams.get("tenantServiceOptionId") || "";
  const tenantServiceOptionKey =
    searchParams.get("tenantServiceOptionKey") || "";

  // Check if current service is Risk Scorecard
  const currentService = tenantServices.find(
    (service) => service.id === tenantServiceId
  );
  const isRiskScorecardActive = currentService?.name === "Risk Scorecard";

  // Get all options for current service
  const currentOptions =
    tenantServices.find((service) => service.id === tenantServiceId)?.options ||
    [];

  // Handle section coming into view
  const handleSectionInView = useCallback(
    (sectionKey: string) => {
      setActiveSection(sectionKey);

      // Update URL when section comes into view
      if (tenantServiceId) {
        const option = currentOptions.find((opt) => opt.key === sectionKey);
        if (option && option.key !== tenantServiceOptionKey) {
          setSearchParams(
            {
              tenantServiceId: tenantServiceId,
              tenantServiceOptionId: option.id,
              tenantServiceOptionKey: option.key,
            },
            { replace: true }
          );
        }
      }
    },
    [tenantServiceId, currentOptions, tenantServiceOptionKey, setSearchParams]
  );

  const mainContentRef = useRef<HTMLDivElement>(null);
  const scrollToSection = useCallback(
    (sectionKey: string) => {
      if (!mainContentRef.current) return;
      const container = mainContentRef.current;
      const element = container.querySelector(`#section-${sectionKey}`);
      if (element) {
        const containerTop = container.getBoundingClientRect().top;
        const elementTop = element.getBoundingClientRect().top;
        const scrollTop = container.scrollTop;
        const yOffset = 80; // adjust if needed
        const targetScrollTop =
          scrollTop + (elementTop - containerTop) - yOffset;
        container.scrollTo({
          top: targetScrollTop,
          behavior: "smooth",
        });
      }
    },
    [mainContentRef]
  );

  // Render individual section content
  const renderSectionContent = useCallback(
    (
      tenantId: string,
      tenantServiceId: string,
      tenantServiceOptionId: string,
      key: string
    ) => {
      if (!tenantId || !tenantServiceId || !tenantServiceOptionId) return null;

      const commonProps = {
        tenantId,
        tenantServiceId,
        tenantServiceOptionId,
      };

      switch (key) {
        case "about":
          return <About {...commonProps} />;
        case "management":
          return <Management {...commonProps} />;
        case "financials_overview":
          return <IncomeBenchmarking />;
        case "key_milestones":
          return <KeyMilestones {...commonProps} />;
        case "credit_analysis":
        // return <BasicCorporateDetails {...commonProps} />;

        // Credit Analysis Sub-Components
        // case "basic_corporate_details":
        //   return "";
        case "financial_profile":
          return <FinancialProfile {...commonProps} />;
        // case "credit_risk_specific_data":
        //   return "";
        // case "portfolio_review":
        case "portfolio_review_management_information":
          return <PortfolioReview {...commonProps} />;
        case "product_level_information":
          return <ProductLevelInformation {...commonProps} />;
        case "credit_assessment_data":
          return <CreditAssessmentData {...commonProps} />;
        case "additional_data":
          return <AdditionalData {...commonProps} />;
        case "esg_analysis":
          return <ESGAnalysis {...commonProps} />;
        case "key":
        // return <Key {...commonProps} />;
        case "client_reviews":
          return <ClientReviews {...commonProps} />;
        case "employee_reviews":
          return <EmployeeReviews {...commonProps} />;
        case "online_presence":
          return <OnlinePresence {...commonProps} />;
        case "linkedin_timeline":
          return <LinkedinTimeline />;
        case "news":
          return <NewsFeed {...commonProps} />;
        case "growth_opportunities":
          return <GrowthOpportunities {...commonProps} />;
        case "tailwinds_headwinds":
          return <TailwindsHeadwindsSection {...commonProps} />;
        case "ma_opportunities":
          return <MAOpportunities />;
        case "ownership":
          return <OwnershipSection {...commonProps} />;
        case "major_shareholders":
        // return <Partners {...commonProps} />;
        case "previous_owners":
          return <PreviousOwners {...commonProps} />;
        case "key_competitors":
          return <KeyCompetitorCard {...commonProps} />;
        case "competitor_map":
          return <CompetitorMap {...commonProps} />;
        case "overview":
          return <Overview {...commonProps} />;
        case "project_info":
          return <RealStateComponent {...commonProps} />;

        case "kyc_kyb_report":
          return <KycKub {...commonProps} />;
        case "bank_risk_score_report":
          return <BankRiskScore {...commonProps} />;
        case "risk_parameters_report":
          return <RiskParameters {...commonProps} />;

        default:
          return <About {...commonProps} />;
      }
    },
    []
  );

  useEffect(() => {
    const fetchTenantServices = async () => {
      if (!tenantId) return;

      try {
        setLoading(true);
        const services = await getTenantServices(tenantId);
        setTenantServices(services);
      } catch (error) {
        console.error("Failed to fetch tenant services:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTenantServices();
  }, [tenantId]);

  useEffect(() => {
    if (
      !loading &&
      tenantServices.length > 0 &&
      !tenantServiceId &&
      !tenantServiceOptionId &&
      !tenantServiceOptionKey
    ) {
      const defaultService = tenantServices[0];
      const defaultOption = defaultService?.options?.[0];

      if (defaultService && defaultOption) {
        setSearchParams(
          {
            tenantServiceId: defaultService.id,
            tenantServiceOptionId: defaultOption.id,
            tenantServiceOptionKey: defaultOption.key,
          },
          { replace: true }
        );
        setActiveSection(defaultOption.key);
      }
    }
  }, [
    loading,
    tenantServices,
    tenantServiceId,
    tenantServiceOptionId,
    tenantServiceOptionKey,
    setSearchParams,
  ]);

  // Set initial active section if not set
  useEffect(() => {
    if (tenantServiceOptionKey && !activeSection) {
      setActiveSection(tenantServiceOptionKey);
    }
  }, [tenantServiceOptionKey, activeSection]);

  if (!tenantId) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Tenant Not Found
          </h1>
          <p className="text-gray-600 mb-4">
            Please provide a valid tenant ID.
          </p>
          <button
            onClick={() => navigate("/")}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      <div className="relative bg-[#FDFDFD] flex flex-col md:flex-row h-screen">
        {/* Mobile Toggle Button */}
        <button
          className="md:hidden fixed top-4 mt-12 left-4 z-50 p-2 bg-white rounded-md shadow-md cursor-pointer"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label="Toggle sidebar"
        >
          {sidebarOpen ? <FiX size={20} /> : <BsThreeDotsVertical size={20} />}
        </button>

        {/* Sidebar - Only show if NOT Risk Scorecard */}
        {!isRiskScorecardActive && (
          <div
            className={`fixed  md:static top-0 left-0 h-full min-w-[250px] transform transition-transform duration-300 z-40
      ${sidebarOpen ? "translate-x-0 bg-white shadow-md" : "-translate-x-full"} 
      md:translate-x-0`}
          >
            {/* Make sidebar content scrollable and grouped */}
            <div className="fixed min-h-screen p-4 flex flex-col justify-center overflow-y-auto max-h-screen space-y-2">
              {tenantServices
                .find((tab) => tab.id === tenantServiceId)
                ?.options?.filter(
                  (option) =>
                    option.key !== "basic_corporate_details" &&
                    option.key !== "credit_risk_specific_data"
                )
                .map((option) => (
                  <div
                    key={option.id}
                    className={`flex items-center justify-between rounded-lg ${tenantServiceOptionId === option.id
                        ? "text-gray-800 font-medium"
                        : "text-gray-500 hover:bg-gray-100"
                      }`}
                  >
                    <button
                      onClick={() => {
                        setSearchParams(
                          {
                            tenantServiceId: tenantServiceId,
                            tenantServiceOptionId: option.id,
                            tenantServiceOptionKey: option.key,
                          },
                          { replace: true }
                        );
                        scrollToSection(option.key);
                        setSidebarOpen(false);
                        setActiveSection(option.key);
                      }}
                      className="text-md text-left px-4 py-1 w-full cursor-pointer flex items-center capitalize"
                    >
                      {/* {option.key.replace(/_/g, " ")} */}
                      {option.key.length > 20
                        ? `${option.key.substring(0, 18).replace(/_/g, " ")}...`
                        : option.key.replace(/_/g, " ")}
                      {tenantServiceOptionId === option.id && (
                        <div className="w-3 h-3 rounded-full bg-gradient-to-br from-pink-500 via-purple-500 to-blue-500 shadow-[0_0_20px_rgba(168,85,247,0.6)] blur-[0.6px] ml-2"></div>
                      )}
                    </button>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Home Button */}
        <div
          className="fixed bottom-23 md:bottom-12 right-4 md:right-12 z-50 p-2 md:p-3 bg-gray-100 text-gray-400 rounded-full shadow-lg cursor-pointer hover:scale-110 transition-transform duration-200"
          onClick={() => navigate("/")}
          role="button"
          tabIndex={0}
          aria-label="Go home"
        >
          <FiHome className="h-4 md:h-6 w-4 md:w-6" />
        </div>

        {/* Main Content */}
        {isRiskScorecardActive ? (
          <main
            ref={mainContentRef}
            className="flex-1 px-2 h-[calc(100vh-4rem)] overflow-y-auto scrollbar-hide "
          >
            <div className="mt-6">
              <RiskScoreLayout />
            </div>
          </main>
        ) : (
          <main
            ref={mainContentRef}
            className="flex-1 px-2 sm:-ml-20 2xl:-ml-60 h-[calc(100vh-4rem)] overflow-y-auto scrollbar-hide "
          >
            <div className="mt-6 mb-50">
              {currentOptions.map((option) => {
                const excludedKeys = [
                  "basic_corporate_details",
                  "credit_risk_specific_data",
                ];

                if (excludedKeys.includes(option.key)) return null;

                return (
                  <ObservedSection
                    key={option.id}
                    sectionKey={option.key}
                    onInView={handleSectionInView}
                    className=""
                  >
                    {renderSectionContent(
                      tenantId,
                      tenantServiceId,
                      option.id,
                      option.key
                    )}
                  </ObservedSection>
                );
              })}
            </div>
          </main>
        )}

        {/* Bottom Tabs */}
        <div className="fixed bottom-0 md:bottom-5 left-1/2 -translate-x-1/2 w-full z-40 backdrop-blur-2xl p-4">
          <div className="w-[95vw] md:max-w-[65vw] mx-auto overflow-auto">
            <div className="flex gap-2 md:gap-3 px-4 bg-[#F6F7F9]/90 rounded-xl p-3 shadow-sm 
                    min-w-max max-w-full overflow-auto justify-center items-center">
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span>Loading tabs...</span>
                </div>
              ) : (
                tenantServices.map((tenantService) => {
                  const isActive = tenantServiceId === tenantService.id;

                  return (
                    <button
                      key={tenantService.id}
                      onClick={() => {
                        const defaultOption = tenantService?.options?.[0];

                        setSearchParams(
                          {
                            tenantServiceId: tenantService.id,
                            tenantServiceOptionId: defaultOption?.id || "",
                            tenantServiceOptionKey: defaultOption?.key || "",
                          },
                          { replace: true }
                        );

                        if (defaultOption) {
                          setActiveSection(defaultOption.key);
                        }
                        if (mainContentRef.current) {
                          mainContentRef.current.scrollTo({
                            top: 0,
                            behavior: "smooth",
                          });
                        }
                      }}
                      className={`px-4 flex items-center py-3 rounded-xl font-medium transition-colors duration-200 
                          whitespace-nowrap cursor-pointer flex-shrink-0 ${isActive
                          ? "bg-white shadow text-gray-900"
                          : "text-gray-700 hover:bg-white hover:shadow"
                        }`}
                    >
                      {isActive && (
                        <div className="relative h-5 w-5 mr-1 flex-shrink-0">
                          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 blur-sm opacity-70" />
                          <div className="relative h-full w-full rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" />
                        </div>
                      )}
                      {tenantService.name}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>

      </div>
    </Layout>
  );
}
